import React, { useState, useEffect } from 'react';
import { bookingAPI } from '../services/api';
import Icon from './Icon';

/** Returns YYYY-MM-DD in local timezone (avoids UTC shift from toISOString) */
function toLocalDateStr(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function AvailableSlotsCalendar({ companyId, serviceTemplateId, onSlotSelect, selectedDate: externalSelectedDate, onDateSelect, preferredTechnicianId }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availableDates, setAvailableDates] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [slotsByDate, setSlotsByDate] = useState({});
  const [selectedDate, setSelectedDate] = useState(externalSelectedDate || '');
  const [selectedSlot, setSelectedSlot] = useState(null);

  useEffect(() => {
    if (externalSelectedDate && externalSelectedDate !== selectedDate) {
      setSelectedDate(externalSelectedDate);
      // If date changes externally, clear selected slot and fetch slots for new date
      setSelectedSlot(null);
      fetchSlotsForDate(externalSelectedDate);
    }
  }, [externalSelectedDate, selectedDate]);

  useEffect(() => {
    if (companyId && (serviceTemplateId || serviceTemplateId === null)) {
      // Allow null serviceTemplateId for default 1-hour bookings
      if (serviceTemplateId === null) {
        // For default bookings, we'll still try to fetch slots with a default duration
        // The backend should handle this case
        fetchAvailableDatesForMonth();
      } else {
        fetchAvailableDatesForMonth();
      }
    } else {
      setAvailableDates(new Set());
      setSlotsByDate({});
      setSelectedDate('');
      setSelectedSlot(null);
    }
  }, [companyId, serviceTemplateId, currentMonth, preferredTechnicianId]);

  const fetchAvailableDatesForMonth = async () => {
    if (!companyId) return;
    // Allow null serviceTemplateId for default 1-hour bookings
    
    setLoading(true);
    try {
      // Check dates for the current month view (plus a few days before/after)
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      
      // Also check a few days before and after the month
      const startDate = new Date(firstDay);
      startDate.setDate(startDate.getDate() - 7);
      const endDate = new Date(lastDay);
      endDate.setDate(endDate.getDate() + 7);
      
      // Don't check past dates
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (startDate < today) {
        startDate.setTime(today.getTime());
      }
      
      const dates = new Set();
      const slotsMap = {};
      
      // Check each day for available slots (limit to 30 days to avoid too many requests)
      const checkDate = new Date(startDate);
      let checkedCount = 0;
      const maxChecks = 30;
      
      while (checkDate <= endDate && checkedCount < maxChecks) {
        const dateStr = toLocalDateStr(checkDate);
        try {
          const response = await bookingAPI.getAvailableSlots(
            parseInt(companyId),
            dateStr,
            serviceTemplateId ? parseInt(serviceTemplateId) : null,
            preferredTechnicianId || null
          );
          const slots = response.data.available_slots || [];
          if (slots.length > 0) {
            dates.add(dateStr);
            slotsMap[dateStr] = slots;
          }
        } catch (err) {
          // Skip dates that fail
          console.error(`Error checking date ${dateStr}:`, err);
        }
        checkDate.setDate(checkDate.getDate() + 1);
        checkedCount++;
      }
      
      setAvailableDates(dates);
      setSlotsByDate(slotsMap);
    } catch (err) {
      console.error('Error fetching available dates:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSlotsForDate = async (dateStr) => {
    if (!companyId || !dateStr) return;
    // Allow null serviceTemplateId for default 1-hour bookings
    
    // If we already have slots for this date, don't fetch again
    if (slotsByDate[dateStr]) {
      return;
    }
    
    try {
      const response = await bookingAPI.getAvailableSlots(
        parseInt(companyId),
        dateStr,
        serviceTemplateId ? parseInt(serviceTemplateId) : null,
        preferredTechnicianId || null
      );
      const slots = response.data.available_slots || [];
      setSlotsByDate(prev => ({
        ...prev,
        [dateStr]: slots
      }));
      if (slots.length > 0) {
        setAvailableDates(prev => new Set([...prev, dateStr]));
      }
    } catch (err) {
      console.error(`Error fetching slots for ${dateStr}:`, err);
    }
  };

  const handleDateClick = (dateStr) => {
    setSelectedDate(dateStr);
    setSelectedSlot(null);
    // Fetch slots for this date if we don't have them yet
    fetchSlotsForDate(dateStr);
    if (onDateSelect) {
      onDateSelect(dateStr);
    }
  };

  const handleSlotClick = (slot) => {
    setSelectedSlot(slot);
    if (onSlotSelect) {
      onSlotSelect(slot);
    }
  };

  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateStr = toLocalDateStr(date);
      days.push({
        day,
        date,
        dateStr,
        isAvailable: availableDates.has(dateStr),
        isToday: dateStr === toLocalDateStr(new Date()),
        isSelected: dateStr === selectedDate
      });
    }
    
    return days;
  };

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
  };

  const days = getDaysInMonth();
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const selectedDateSlots = selectedDate ? slotsByDate[selectedDate] || [] : [];

  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 480;

  return (
    <div style={{ width: '100%', maxWidth: '600px' }}>
      {/* Calendar Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        padding: isMobile ? '12px 8px' : '10px 12px',
        background: 'var(--bg-overlay-light)',
        borderRadius: '10px',
        gap: '4px'
      }}>
        <button
          onClick={previousMonth}
          className="btn btn-secondary btn-sm"
          style={{ padding: isMobile ? '10px 12px' : '8px 16px', minWidth: '44px', minHeight: '44px' }}
        >
          ←
        </button>
        <h3 style={{ margin: 0, color: '#ffffff', fontSize: isMobile ? '15px' : '18px', textAlign: 'center', flex: 1 }}>
          {months[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>
        <div style={{ display: 'flex', gap: '4px' }}>
          <button
            onClick={goToToday}
            className="btn btn-secondary btn-sm"
            style={{ padding: isMobile ? '10px 10px' : '8px 16px', minWidth: '44px', minHeight: '44px', fontSize: isMobile ? '12px' : '14px' }}
          >
            Today
          </button>
          <button
            onClick={nextMonth}
            className="btn btn-secondary btn-sm"
            style={{ padding: isMobile ? '10px 12px' : '8px 16px', minWidth: '44px', minHeight: '44px' }}
          >
            →
          </button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: isMobile ? '3px' : '4px',
        marginBottom: '16px'
      }}>
        {/* Week day headers */}
        {weekDays.map(day => (
          <div
            key={day}
            style={{
              textAlign: 'center',
              padding: isMobile ? '8px 2px' : '6px 4px',
              color: 'rgba(255,255,255,0.6)',
              fontSize: isMobile ? '10px' : '11px',
              fontWeight: '600',
              textTransform: 'uppercase'
            }}
          >
            {isMobile ? day.charAt(0) : day}
          </div>
        ))}

        {/* Calendar days */}
        {days.map((dayData, index) => {
          if (!dayData) {
            return <div key={`empty-${index}`} />;
          }

          const { day, dateStr, isAvailable, isToday, isSelected } = dayData;
          const isPast = dateStr < toLocalDateStr(new Date());

          return (
            <button
              key={dateStr}
              onClick={() => !isPast && handleDateClick(dateStr)}
              disabled={isPast}
              style={{
                padding: isMobile ? '12px 2px' : '10px 4px',
                borderRadius: '6px',
                border: isSelected
                  ? '2px solid var(--lime-primary)'
                  : isToday
                  ? '2px solid rgba(61, 90, 32, 0.5)'
                  : '1px solid rgba(255,255,255,0.08)',
                background: isSelected
                  ? 'rgba(61, 90, 32, 0.25)'
                  : isAvailable && !isPast
                  ? 'rgba(16, 185, 129, 0.1)'
                  : 'rgba(255,255,255,0.02)',
                color: isPast
                  ? 'rgba(255,255,255,0.3)'
                  : isSelected
                  ? '#4A8C18'
                  : 'rgba(255,255,255,0.8)',
                cursor: isPast ? 'not-allowed' : 'pointer',
                fontSize: isMobile ? '14px' : '13px',
                fontWeight: isSelected || isToday ? '700' : '400',
                position: 'relative',
                transition: 'all 0.15s',
                minHeight: isMobile ? '44px' : '36px',
                touchAction: 'manipulation'
              }}
              onMouseEnter={(e) => {
                if (!isPast && !isSelected) {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                  e.currentTarget.style.borderColor = 'rgba(160, 200, 20, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isPast && !isSelected) {
                  e.currentTarget.style.background = isAvailable ? 'rgba(16, 185, 129, 0.1)' : 'rgba(255,255,255,0.02)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
                }
              }}
            >
              {day}
              {isAvailable && !isPast && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: isMobile ? '4px' : '3px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: isMobile ? '6px' : '4px',
                    height: isMobile ? '6px' : '4px',
                    borderRadius: '50%',
                    background: '#4A8C18'
                  }}
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Selected Date Time Slots */}
      {selectedDate && selectedDateSlots.length > 0 && (
        <div style={{
          marginTop: '16px',
          padding: '16px',
          background: 'var(--bg-overlay-light)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: '12px'
        }}>
          <h4 style={{ margin: '0 0 12px 0', color: '#ffffff', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icon name="calendar" color="primary" darkMode={true} size={16} />
            {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-GB', {
              weekday: 'long',
              month: 'long',
              day: 'numeric'
            })}
          </h4>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(120px, 1fr))',
            gap: isMobile ? '6px' : '8px'
          }}>
            {selectedDateSlots.map((slot, index) => {
              const isSlotSelected = selectedSlot && selectedSlot.start === slot.start;
              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleSlotClick(slot)}
                  style={{
                    padding: isMobile ? '12px 8px' : '8px 14px',
                    borderRadius: '8px',
                    border: isSlotSelected
                      ? '2px solid var(--lime-primary)'
                      : '1px solid rgba(255,255,255,0.1)',
                    background: isSlotSelected
                      ? 'rgba(61, 90, 32, 0.2)'
                      : 'rgba(255,255,255,0.03)',
                    color: isSlotSelected ? '#4A8C18' : '#ffffff',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.15s',
                    fontSize: isMobile ? '14px' : '13px',
                    fontWeight: isSlotSelected ? '600' : '400',
                    minHeight: isMobile ? '48px' : '36px',
                    touchAction: 'manipulation'
                  }}
                  onMouseEnter={(e) => {
                    if (!isSlotSelected) {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSlotSelected) {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
                    }
                  }}
                >
                  {slot.start} - {slot.end}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {selectedDate && selectedDateSlots.length === 0 && !loading && (
        <div style={{
          marginTop: '16px',
          padding: '16px',
          background: 'var(--bg-overlay-light)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: '12px',
          textAlign: 'center',
          color: 'rgba(255,255,255,0.5)',
          fontSize: '13px'
        }}>
          No available time slots on this day. Please choose another date.
        </div>
      )}

      {loading && (
        <div style={{
          marginTop: '16px',
          padding: '16px',
          textAlign: 'center',
          color: 'rgba(255,255,255,0.5)',
          fontSize: '13px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}>
          <Icon name="loading" color="secondary" darkMode={true} size={16} />
          Loading available times...
        </div>
      )}

      {/* Warning when no slots are available for the entire calendar */}
      {!loading && availableDates.size === 0 && (
        <div style={{
          marginTop: '16px',
          padding: '20px',
          background: 'rgba(245, 158, 11, 0.1)',
          border: '1px solid rgba(245, 158, 11, 0.3)',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <Icon name="calendar" color="warning" darkMode={true} size={32} style={{ marginBottom: '12px' }} />
          <p style={{ 
            margin: '0 0 8px 0', 
            color: '#fbbf24', 
            fontWeight: '600',
            fontSize: '15px'
          }}>
            No online booking slots available
          </p>
          <p style={{ 
            margin: 0, 
            color: 'rgba(255,255,255,0.6)', 
            fontSize: '13px' 
          }}>
            This service shop is currently not accepting online bookings, or all slots are fully booked.
            Please try again later or contact the shop directly by phone.
          </p>
        </div>
      )}
    </div>
  );
}

export default AvailableSlotsCalendar;

